import React, { useState, useContext } from 'react';
import { ReactComponent as RedCrossIcon } from 'assets/icon/x-circle-red.svg';
import { ReactComponent as CrossIcon } from 'assets/icon/x-circle.svg';
import CloudUpload from 'components/common/cloud-upload';
import PrimaryDarkButton from 'components/common/buttons/primary-dark-btn';
import DarkButton from 'components/common/buttons/dark-btn';
import { useAppDispatch, useAppSelector } from 'hooks/useAppDispatchSelector';
import { initModal, appointmentStatusEnum } from 'helpers/utils';
import { setModal } from 'store/features/modelSlice';
import { ReactComponent as CancelIcon } from 'assets/icon/cancel.svg';
import { ReactComponent as PlayIcon } from 'assets/icon/play.svg';
import { setPreviewFileData } from 'store/features/modelSlice';
import { ReactComponent as SearchIcon } from 'assets/icon/search.svg';
import Textarea from 'components/common/textarea-field';
import { AppContext } from 'context/app-context';
import {
  IChangeWorkshopAppointmentStatusRequest,
  uploadWorkshopMedia,
  changeWorkshopAppointmentStatus,
} from 'store/features/workshopAppointmentsSlice';
import { IUploadMediaResponse } from 'components/sidemodal/add-edit-workshop-appointment';
import { IUploadImageRequestParam } from 'store/features/organizationSlice';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const CancelledWorkshopAppointment: React.FC = () => {
  const dispatch = useAppDispatch();
  const { workshopAppointmentDetails } = useAppSelector(state => state.workShopAppointment);
  const {
    modal: {
      content: { callBackFun },
    },
  } = useAppSelector(state => state.modal);
  const { setLoader } = useContext(AppContext);
  const [files, setFiles] = useState<File[]>([]);
  const [reason, setReason] = useState<string>('');

  const { t } = useTranslation();
  const handleSetFiles = (file: File) => {
    const updatedFiles = [...files, file];
    setFiles(updatedFiles);
  };
  const handlePreviewFile = (file: File, type: string) => {
    dispatch(
      setPreviewFileData({
        type: type || '',
        content: { url: window.URL.createObjectURL(file) },
      }),
    );
  };
  const getFileContent = (file: File) => {
    const splitedVal = file.type.split('/');
    switch (splitedVal[0]) {
      case 'image':
        return (
          <img
            src={window.URL.createObjectURL(file)}
            className="flex items-center justify-center min-w-full h-full w-12 object-contain"
            alt=""
            onClick={() => handlePreviewFile(file, splitedVal[0])}
          />
        );
      case 'video':
        return (
          <div
            className="flex items-center justify-center bg-grey3 w-12 h-full min-w-full rounded-3 cursor-pointer"
            onClick={() => handlePreviewFile(file, splitedVal[0])}
          >
            <PlayIcon />
          </div>
        );
      default:
        return (
          <div
            className="flex items-center justify-center bg-grey1 w-12 min-w-full h-full rounded-3 cursor-pointer"
            onClick={() => handlePreviewFile(file, splitedVal[0])}
          >
            <SearchIcon />
          </div>
        );
    }
  };
  const handleRemoveFiles = (index: number) => {
    const updatedFiles = (files || []).filter((file, i) => i !== index);
    setFiles(updatedFiles);
  };
  const uploadMediaForAdd = async () => {
    try {
      const uploadMediaParam =
        files.map(data => {
          return {
            request: {
              file_name: data?.name || '',
              file_type: data?.type || '',
              file_size: data?.size || 0,
            },
            file: data,
          };
        }) || [];
      const mediaResponse: IUploadMediaResponse[] = await Promise.all(
        [...uploadMediaParam].map(data => {
          const params: IUploadImageRequestParam = {
            request: {
              file_name: data?.request?.file_name || '',
              file_type: data?.request?.file_type || '',
              file_size: data?.request?.file_size || 0,
            },
            file: data?.file,
          };
          const res = dispatch(uploadWorkshopMedia({ params }));
          return res;
        }),
      );
      return Promise.resolve(mediaResponse);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!reason) {
        toast.error(t('workshopAppointment.cancelled.errorResonToastMessage'));
        return;
      }
      setLoader(true);
      const uploadMedia = await uploadMediaForAdd().catch(err => {
        return;
      });
      let request: IChangeWorkshopAppointmentStatusRequest = {
        admin_media: uploadMedia?.length ? uploadMedia.map(data => data?.key || '') : [],
        appointment_id: workshopAppointmentDetails?._id || '',
        status: appointmentStatusEnum?.CANCELED,
        reject_reason: reason,
      };

      dispatch(changeWorkshopAppointmentStatus({ request: { ...request } }))
        .then(() => {
          callBackFun && callBackFun();
          toast.success(t(''));
          dispatch(setModal(initModal));
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
    } catch (error) {
      setLoader(false);
    }
  };
  return (
    <div className="flex flex-wrap max-w-[450px] w-[450px] gap-y-4 overflow-hidden">
      <div className="flex items-center w-full">
        <div className="flex items-center flex-1">
          <RedCrossIcon height={40} width={40} />
          <label className="pl-2 text-xl font-semibold text-black2">
            {t('workshopAppointment.cancelled.rejectTitle')}
          </label>
        </div>
        <div
          className="flex items-center justify-center cursor-pointer"
          onClick={() => dispatch(setModal(initModal))}
        >
          <CrossIcon height={32} width={32} />
        </div>
      </div>
      <div className="flex flex-wrap w-full overflow-y-auto max-h-[calc(100vh- 200px)]  gap-y-4">
        <div className="flex w-full">
          <CloudUpload
            setFile={e => {
              if (e?.target?.files?.length) {
                handleSetFiles(e?.target?.files[0]);
              } else {
                e.stopPropagation();
              }
            }}
            title={t('workshopAppointment.steps.uploadFileTitle')}
          />
        </div>
        {files && files?.length ? (
          <div className="w-full flex items-center justify-start overflow-hidden">
            <div className="overflow-x-auto w-full max-w-full flex py-2">
              {[...files].map((file, index) => (
                <div
                  className="flex items-center justify-center ml-2 rounded-3 h-12 w-12 max-w-12 min-h-full min-w-fit bg-grey1 relative"
                  key={`${file?.name}-${index}`}
                >
                  <CancelIcon
                    className="flex absolute -top-1 -right-1 cursor-pointer"
                    onClick={() => handleRemoveFiles(index)}
                  />
                  {getFileContent(file)}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <></>
        )}
        <div className="w-full">
          <label className="font-medium text-sm text-black2 w-full">
            {t('workshopAppointment.cancelled.reason')}
          </label>
          <Textarea
            placeholder={t('workshopAppointment.cancelled.reasonPlaceholder')}
            value={reason}
            onChange={val => setReason(val)}
            row={2}
          />
        </div>
      </div>
      <div className="flex items-center justify-center w-full gap-x-4 mt-4">
        <PrimaryDarkButton
          label={t('CANCEL')}
          onClick={() => dispatch(setModal(initModal))}
          width="w-1/2"
          background="bg-red-500"
        />
        <DarkButton
          label={t('workshopAppointment.cancelled.primaryButtonLabel')}
          width="w-1/2"
          onClick={() => handleSubmit()}
        
        />
      </div>
    </div>
  );
};

export default CancelledWorkshopAppointment;
